import type { ComponentProps, JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';

/**
 * A row's outcome as bulk import moves through parse → (optional) create: `ready`/`too-long`/
 * `invalid` come out of parsing (see `SkillEditorDialog`'s file handler), `importing`/`done`/
 * `failed` are set as `runBulkImport` walks the accepted rows.
 */
export type BulkImportRowStatus =
  'ready' | 'too-long' | 'invalid' | 'importing' | 'done' | 'failed';

/** One parsed `.md`/`.txt` file, as rendered by `BulkImportPreview` and driven by `SkillEditorDialog`. */
export interface BulkImportRow {
  fileName: string;
  trigger: string;
  title: string;
  description: string;
  instructions: string;
  /** The file's preserved frontmatter lines (`parseFrontmatter`'s `extra`), sent as `frontmatter_extra`. */
  frontmatterExtra?: string | null;
  status: BulkImportRowStatus;
  /** Extra line under the status badge — the too-long/invalid reason, or a create failure message. */
  detail?: string;
}

const STATUS_LABEL: Record<BulkImportRowStatus, string> = {
  ready: 'Ready',
  'too-long': 'Too long',
  invalid: 'Invalid',
  importing: 'Importing…',
  done: 'Imported',
  failed: 'Failed',
};

const STATUS_BADGE_VARIANT: Record<
  BulkImportRowStatus,
  NonNullable<ComponentProps<typeof Badge>['variant']>
> = {
  ready: 'outline',
  'too-long': 'warning',
  invalid: 'warning',
  importing: 'secondary',
  done: 'success',
  failed: 'destructive',
};

/** True once a row can no longer change on its own — it either never qualified for import or the import attempt already settled. Exported so `SkillEditorDialog` can decide whether its Cancel/Close button should reopen the file picker or close the whole dialog. */
export function isBulkRowSettled(status: BulkImportRowStatus): boolean {
  return (
    status === 'too-long' ||
    status === 'invalid' ||
    status === 'done' ||
    status === 'failed'
  );
}

/**
 * Per-file preview table for a multi-file `.md`/`.txt` bulk import. Purely presentational —
 * `SkillEditorDialog` owns parsing, collision auto-suffixing, and the sequential `createSkill`
 * calls; this component only renders the rows it is given and forwards the Import/Cancel clicks.
 */
export function BulkImportPreview({
  rows,
  importing,
  onImport,
  onCancel,
}: {
  rows: BulkImportRow[];
  importing: boolean;
  onImport: () => void;
  onCancel: () => void;
}): JSX.Element {
  const importableCount = rows.filter(
    (row) => row.status !== 'too-long' && row.status !== 'invalid',
  ).length;
  const doneCount = rows.filter((row) => row.status === 'done').length;
  const allSettled = rows.every((row) => isBulkRowSettled(row.status));

  return (
    <div className="flex flex-col gap-3">
      <div className="max-h-64 overflow-y-auto rounded-lg border border-border/60">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-muted/60 text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">File</th>
              <th className="px-3 py-2 font-medium">Trigger</th>
              <th className="px-3 py-2 font-medium">Title</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {rows.map((row, index) => (
              <tr key={`${index}-${row.fileName}`}>
                <td
                  className="max-w-40 truncate px-3 py-2 text-muted-foreground"
                  title={row.fileName}
                >
                  {row.fileName}
                </td>
                <td className="px-3 py-2 font-mono">/{row.trigger}</td>
                <td className="max-w-48 truncate px-3 py-2">{row.title}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-col gap-0.5">
                    <Badge variant={STATUS_BADGE_VARIANT[row.status]}>
                      {STATUS_LABEL[row.status]}
                    </Badge>
                    {row.detail && (
                      <span className="text-[11px] text-muted-foreground">
                        {row.detail}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {allSettled
            ? `${doneCount} of ${importableCount} imported.`
            : `${importableCount} of ${rows.length} file(s) ready to import.`}
        </p>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={importing}
          >
            {allSettled ? 'Close' : 'Cancel'}
          </Button>
          {!allSettled && (
            <Button
              size="sm"
              onClick={onImport}
              disabled={importing || importableCount === 0}
            >
              {importing ? 'Importing…' : `Import ${importableCount}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
